import React from "react";
import { AppLink } from "../../../components/ui/link";
import type { MainAppPageProps } from "../types";

type TermsPageProps = Pick<MainAppPageProps, "routes">;

const SECTION_CLASS = "mb-8 last:mb-0";
const HEADING_CLASS = "mb-3 font-display text-lg font-semibold text-foreground";
const PARA_CLASS = "mb-3 text-sm leading-relaxed text-muted-foreground";

export const TermsPage: React.FC<TermsPageProps> = ({ routes }) => (
  <article className="mx-auto max-w-3xl">
    <h1 className="mb-8 font-display text-2xl font-semibold tracking-tight text-foreground">
      Terms of Service
    </h1>
    <p className="mb-8 text-sm leading-relaxed text-muted-foreground">
      Last updated: October 31, 2026
    </p>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>1. Acceptance of Terms</h2>
      <p className={PARA_CLASS}>
        By accessing or using ToolKit, you agree to be bound by these Terms of
        Service. If you do not agree, do not use the service.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>2. Description of Service</h2>
      <p className={PARA_CLASS}>
        ToolKit is organized around an Organization account. Each Organization
        includes the full suite: a contractor marketing website (quotes, design,
        and related tools), job-site photos (capture, geofence, tags, and
        library management), and credentials (license and certification
        tracking, reminders, sharing, and export). The website builder is
        available on the web and in the mobile app. Job-site photos and
        credentials are available on web and mobile and are scoped to the
        Organization. We reserve the right to modify, suspend, or discontinue
        any part of the service at any time without notice.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>
        3. Organizations, Memberships, and Invites
      </h2>
      <p className={PARA_CLASS}>
        Your Organization is the top account. Users belong to an Organization
        through memberships with roles of owner, admin, member, or viewer. The
        system can associate a user with more than one Organization; the current
        product experience is built around one Organization per user. Owners and
        admins may use backend features, including website building and photo
        library management. Regular members may upload job-site photos and
        manage their own credentials. Viewers have a more limited, read-oriented
        role for job photos, and may still keep their own credentials.
      </p>
      <p className={PARA_CLASS}>
        Owners and admins may invite others by sending an invitation link. An
        invite can create a new ToolKit user and add them to the Organization,
        or join an existing user. You are responsible for the people you invite,
        the roles you assign, and the content they contribute.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>4. No Warranties</h2>
      <p className={PARA_CLASS}>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
        WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
        LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
        SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL
        COMPONENTS.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>5. Limitation of Liability</h2>
      <p className={PARA_CLASS}>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, TOOLKIT AND ITS AFFILIATES,
        OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
        INCLUDING LOST PROFITS, LOST DATA, OR BUSINESS INTERRUPTION, ARISING
        FROM YOUR USE OF OR INABILITY TO USE THE SERVICE. IN NO EVENT SHALL OUR
        TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS
        PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS
        GREATER.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>6. Your Content and Conduct</h2>
      <p className={PARA_CLASS}>
        You are solely responsible for all content you and your Organization
        members publish through ToolKit, including website business information,
        images, job-site photos and videos, tags, and customer communications.
        You represent that you have the right to use such content and that it
        does not violate any third-party rights or applicable laws. We are not
        responsible for the accuracy, legality, or appropriateness of
        user-generated content.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>
        7. AI-Generated Content and Automated SEO Pages
      </h2>
      <p className={PARA_CLASS}>
        ToolKit uses third-party large language models (&ldquo;LLMs&rdquo;) to
        automatically generate copy for portions of your contractor website,
        including but not limited to per-service pages, per-service-area pages,
        service-area combination pages, FAQ pages, blog content, and other
        SEO-focused pages or sections (collectively, &ldquo;AI-Generated
        Content&rdquo;). Generation is performed using business information you
        provide (services, service areas, FAQs, business name, contact details,
        etc.) and general industry context. We instruct the model to avoid
        fabricating awards, certifications, tenure, or other unverifiable
        claims, and we apply automated validators, but no automated system is
        perfect.
      </p>
      <p className={PARA_CLASS}>
        AI-Generated Content is published to your site automatically and is
        treated as &ldquo;your content&rdquo; under Section 6 the moment it
        appears on a site you control. You are responsible for reviewing
        AI-Generated Content displayed on your site, correcting any
        inaccuracies, and removing or editing anything you do not want to
        publish. ToolKit provides controls in your dashboard to update the
        underlying business inputs, which automatically refreshes the relevant
        AI-Generated Content on your next page load. If you identify content you
        believe is inaccurate, misleading, or otherwise problematic, contact us
        and we will remove it.
      </p>
      <p className={PARA_CLASS}>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, TOOLKIT, ITS AFFILIATES, AND THE
        PROVIDERS OF THE UNDERLYING AI MODELS DISCLAIM ALL WARRANTIES REGARDING
        AI-GENERATED CONTENT, INCLUDING ITS ACCURACY, COMPLETENESS, FITNESS FOR
        A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND COMPLIANCE WITH APPLICABLE
        LAW (INCLUDING ADVERTISING, CONSUMER PROTECTION, AND TRADE LICENSING
        LAWS). YOU ACKNOWLEDGE THAT AI MODELS CAN PRODUCE INACCURATE, OUTDATED,
        OR INAPPROPRIATE OUTPUT, AND YOU AGREE TO INDEMNIFY AND HOLD US HARMLESS
        FROM ANY CLAIMS ARISING OUT OF AI-GENERATED CONTENT THAT REMAINS
        PUBLISHED ON YOUR SITE.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>8. Third-Party Services</h2>
      <p className={PARA_CLASS}>
        The service may integrate with third-party services (e.g., payment
        processors, analytics). Your use of those services is subject to their
        respective terms. We are not responsible for third-party services or
        their availability.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>9. Payment and Billing</h2>
      <p className={PARA_CLASS}>
        New Organizations start with a 30-day trial that includes the full suite
        (website, job-site photos, and credentials). After the trial, the suite
        is $9/month. Subscription fees are billed in advance. Web and Android
        billing may be processed through Stripe, while iPhone and iPad billing
        may be processed through Apple&rsquo;s App Store in-app subscription
        flow. Refunds are handled in accordance with the provider used for the
        purchase and remain at our discretion where the provider permits. We may
        change pricing with reasonable notice. Continued use after a price
        change constitutes acceptance.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>10. Termination</h2>
      <p className={PARA_CLASS}>
        We may suspend or terminate your Organization or user account at any
        time for violation of these terms or for any other reason. You may
        delete your user account at any time in Settings (web) or Profile
        (Toolkit app). Account deletion is permanent; you will need a new
        account to use ToolKit again. Job sites, photos, and other company
        content you added remain with the Organization. Upon termination, your
        right to use the service ceases immediately.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>11. Dispute Resolution</h2>
      <p className={PARA_CLASS}>
        Any disputes arising from these terms or the service shall be resolved
        through binding arbitration in accordance with the rules of the ADR
        Institute of Canada, Inc., and you waive any right to participate in a
        class action.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>12. Governing Law</h2>
      <p className={PARA_CLASS}>
        These terms are governed by the laws of Canada and the Province of
        Ontario, without regard to conflict of law principles.
      </p>
    </section>

    <section className={SECTION_CLASS}>
      <h2 className={HEADING_CLASS}>13. Changes</h2>
      <p className={PARA_CLASS}>
        We may update these terms from time to time. Material changes will be
        posted on this page with an updated date. Continued use after changes
        constitutes acceptance.
      </p>
    </section>

    <p className="mt-8 text-sm text-muted-foreground">
      Questions? See our{" "}
      <AppLink to={routes.faq} variant="premium">
        FAQ
      </AppLink>{" "}
      or{" "}
      <AppLink to={routes.privacy} variant="premium">
        Privacy Policy
      </AppLink>
      .
    </p>
  </article>
);
